import { useState } from "react";
import { useQuery } from "react-apollo";
import moment from "moment";

import { GET_BEST_TIME_SLICE_WEB_MERCATOR_PNGS } from "../GraphQL/GetBestTimeSliceWebMercatorPng";

export type LngLatBounds = {
  minLongitude: number;
  minLatitude: number;
  maxLongitude: number;
  maxLatitude: number;
};

export type ColorScale = Array<{
  pixelValue: number;
  red: number;
  green: number;
  blue: number;
  alpha: number;
}>;

const getQueryTimestamp = (
  trailTimestamp: string,
  selectedVariableId: number
) => {
  let queryTimestamp = trailTimestamp;
  let hycomMoment = moment(queryTimestamp).utc();
  let hourPart = hycomMoment.hour();

  if (selectedVariableId === 51) {
    // TODO: very brittle, need to remember to hardcode
    hourPart = Math.floor(hourPart / 3) * 3;
  }

  hycomMoment.set({ hour: hourPart, minute: 0, second: 0, ms: 0 });

  return hycomMoment.utc().toISOString();
};

// TODO: Lift this up as we build more general-purpose data fetching hooks
type RemoteDataState<TData> = {
  status: "loading" | "errored" | "ready";
  data?: TData;
  error?: string;
};

type SofarForecastPNGOptions = {
  bounds: LngLatBounds;
  dataSourceId: number;
  variableId: number;
  colorScale: ColorScale;
  timestamp: string;
};
type ForecastPNGData = {
  pngDataUrl: string;
  instant: string;
  leadTime: string;
  /** lng/lat array of the forecast data boundary corners*/
  corners: [
    [number, number],
    [number, number],
    [number, number],
    [number, number]
  ];
};
/** A hook that handles fetching a forecast PNG from the Sofar API */
const useSofarForecastPNG = ({
  bounds,
  dataSourceId,
  variableId,
  colorScale,
  timestamp
}: SofarForecastPNGOptions) => {
  const [forecastData, setForecastData] = useState<
    RemoteDataState<ForecastPNGData>
  >({
    status: "loading"
  });

  useQuery(GET_BEST_TIME_SLICE_WEB_MERCATOR_PNGS, {
    context: { clientName: "wexGeo" },
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        boundary: [
          {
            longitude: bounds.minLongitude,
            latitude: bounds.minLatitude
          },
          {
            longitude: bounds.maxLongitude,
            latitude: bounds.minLatitude
          },
          {
            longitude: bounds.maxLongitude,
            latitude: bounds.maxLatitude
          },
          {
            longitude: bounds.minLongitude,
            latitude: bounds.maxLatitude
          },
          {
            longitude: bounds.minLongitude,
            latitude: bounds.minLatitude
          }
        ],
        dataSourceId,
        instant: getQueryTimestamp(timestamp, variableId),
        variableId,
        colorScale
      }
    },
    onError: e => {
      setForecastData({ status: "errored", error: e.message });
    },
    onCompleted: response => {
      if (response.png) {
        setForecastData({
          status: "ready",
          data: {
            pngDataUrl: `data:image/png;base64,${response.png.data}`,
            corners: response.png.geojsonEnvelope.coordinates[0].slice(0, 4),
            leadTime: response.png.leadTime,
            instant: response.png.instant
          }
        });
      } else {
        setForecastData({ status: "errored", error: "No PNG data received." });
      }
    }
  });

  return forecastData;
};

export default useSofarForecastPNG;
